import { useState, useMemo, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  MapPin,
  MessageSquare,
  Send,
  Sparkles,
  Ticket,
  Users,
} from 'lucide-react'
import { ApiClientError } from '@/lib/api-client'
import { artistFetch } from '@/lib/artist-api'
import { AsyncBoundary } from '@/components/AsyncBoundary'
import { HiveCellsPanel } from '@/components/HiveCellsPanel'
import { cn } from '@/lib/utils'

// ─── Types mirroring ShowBrief + OutstandingItem from the API ────────────────

interface ShowBrief {
  show: {
    id: string
    title: string
    date: string
    showTime: string
    doorsTime: string | null
    venue: string
    city: string
    country: string
    capacityTotal: number
    capacitySold: number
    feeGbp: number | null
    stage: string
    climateChecked: boolean
    climateApproved: boolean | null
    integrations: {
      hiveCells?: {
        showId?: string
        status?: string
        syncedAt?: string
        lastUpdatedAt?: string
      }
    } | null
  }
  inquiry: {
    contactName: string | null
    contactOrg: string | null
    budgetMin: number | null
    budgetMax: number | null
    dueDiligenceScore: string | null
  } | null
  roleRequirements: {
    total: number
    filled: number
    offered: number
    unfilled: number
    venueManaged: number
  }
  crewEngagements: {
    offered: number
    accepted: number
    declined: number
    items: Array<{
      id: string
      role: string
      status: string
      crewName: string
      dayRateMinor: number | null
    }>
  }
  emailThreads: {
    open: number
    awaitingReply: number
    replied: number
    resolved: number
    items: Array<{
      id: string
      subject: string
      status: string
      messageCount: number
      lastMessageAt: string
      assignedMemberName: string | null
    }>
  }
  brokerThreads: Array<{
    id: string
    brokerName: string
    subject: string
    messageCount: number
    lastActivity: string
  }>
  ticketing: {
    totalSold: number
    totalCapacity: number
    sections: Array<{
      id: string
      name: string
      capacity: number
      sold: number
      held: number
      pricePence: number
    }>
  }
  travelLegs: Array<{
    id: string
    type: string
    origin: string
    destination: string
    departureAt: string
    carrier: string | null
    partyCount: number
  }>
  marketingPosts: {
    draft: number
    scheduled: number
    published: number
    failed: number
  }
  setlist: { id: string; version: number; trackCount: number } | null
  debrief: {
    id: string
    venueRating: number
    promoterRating: number
    soundRating: number
    logisticsRating: number
  } | null
}

interface OutstandingItem {
  type: string
  severity: 'low' | 'medium' | 'high'
  title: string
  description: string
  linkedEntityType?: string
  linkedEntityId?: string
  relatedDate?: string
}

interface AskResponse {
  answer: string
  contextSummary: {
    showTitle: string
    emailThreadCount: number
    outstandingItemCount: number
  }
}

type Tab = 'overview' | 'outstanding' | 'ask'

const SEVERITY_COLOUR: Record<OutstandingItem['severity'], string> = {
  high: 'border-rose-300 bg-rose-50',
  medium: 'border-amber-300 bg-amber-50',
  low: 'border-slate-300 bg-slate-50',
}

const SEVERITY_ICON_COLOUR: Record<OutstandingItem['severity'], string> = {
  high: 'text-rose-600',
  medium: 'text-amber-600',
  low: 'text-slate-500',
}

export function ShowDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const [tab, setTab] = useState<Tab>('overview')

  const brief = useQuery<ShowBrief, ApiClientError>({
    queryKey: ['show-brief', id],
    queryFn: () => artistFetch<ShowBrief>(`/shows/${id}/brief`),
    enabled: !!id,
  })

  const outstanding = useQuery<OutstandingItem[], ApiClientError>({
    queryKey: ['show-outstanding', id],
    queryFn: () => artistFetch<OutstandingItem[]>(`/shows/${id}/outstanding`),
    enabled: !!id,
  })

  const show = brief.data?.show
  const daysUntil = useMemo(() => {
    if (!show) return null
    return Math.ceil(
      (new Date(show.date).getTime() - Date.now()) / (24 * 60 * 60 * 1000),
    )
  }, [show])

  return (
    <div>
      <Link
        to="/shows"
        className="mb-4 inline-flex items-center gap-1 text-sm text-ovation-muted hover:text-ovation-ink"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to shows
      </Link>

      <AsyncBoundary isLoading={brief.isLoading} error={brief.error}>
        {brief.data && show && (
          <>
            {/* Hero */}
            <div className="mb-6 rounded-lg border border-ovation-ink/10 bg-white p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ovation-muted">
                    <Calendar className="h-3 w-3" />
                    {new Date(show.date).toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                    {daysUntil !== null && daysUntil > 0 && (
                      <span className="text-ovation-accent">
                        · in {daysUntil} days
                      </span>
                    )}
                  </div>
                  <h1 className="mt-1 font-display text-4xl tracking-tight">
                    {show.title}
                  </h1>
                  <div className="mt-1 flex items-center gap-2 text-sm text-ovation-muted">
                    <MapPin className="h-3 w-3" />
                    {show.venue}, {show.city}, {show.country}
                  </div>
                </div>
                <div className="text-right">
                  <StageBadge stage={show.stage} />
                  {outstanding.data && outstanding.data.length > 0 && (
                    <div className="mt-2 inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800">
                      <AlertTriangle className="h-3 w-3" />
                      {outstanding.data.length} outstanding
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <HeroStat
                  icon={<Users className="h-4 w-4" />}
                  label="Crew confirmed"
                  value={`${brief.data.crewEngagements.accepted} / ${
                    brief.data.crewEngagements.accepted +
                    brief.data.crewEngagements.offered
                  }`}
                />
                <HeroStat
                  icon={<CheckCircle2 className="h-4 w-4" />}
                  label="Roles filled"
                  value={`${brief.data.roleRequirements.filled} / ${brief.data.roleRequirements.total}`}
                />
                <HeroStat
                  icon={<Ticket className="h-4 w-4" />}
                  label="Tickets sold"
                  value={
                    brief.data.ticketing.totalCapacity > 0
                      ? `${brief.data.ticketing.totalSold} / ${brief.data.ticketing.totalCapacity}`
                      : '—'
                  }
                />
                <HeroStat
                  icon={<MessageSquare className="h-4 w-4" />}
                  label="Email threads"
                  value={brief.data.emailThreads.items.length}
                />
              </div>
            </div>

            {/* Tabs */}
            <div className="mb-4 flex gap-1 border-b border-ovation-ink/10">
              <TabButton active={tab === 'overview'} onClick={() => setTab('overview')}>
                Overview
              </TabButton>
              <TabButton
                active={tab === 'outstanding'}
                onClick={() => setTab('outstanding')}
              >
                Outstanding {outstanding.data && `(${outstanding.data.length})`}
              </TabButton>
              <TabButton active={tab === 'ask'} onClick={() => setTab('ask')}>
                <Sparkles className="mr-1 inline h-3 w-3" />
                Ask the show
              </TabButton>
            </div>

            {tab === 'overview' && <OverviewTab brief={brief.data} />}
            {tab === 'outstanding' && (
              <OutstandingTab
                items={outstanding.data ?? []}
                isLoading={outstanding.isLoading}
                showId={id}
              />
            )}
            {tab === 'ask' && <AskTab showId={id} />}
          </>
        )}
      </AsyncBoundary>
    </div>
  )
}

// ─── Tabs ────────────────────────────────────────────────────────────────────

function OverviewTab({ brief }: { brief: ShowBrief }) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Panel title="Inquiry">
        {brief.inquiry ? (
          <dl className="space-y-1 text-sm">
            <DtDd label="Contact" value={brief.inquiry.contactName ?? '—'} />
            <DtDd label="Organisation" value={brief.inquiry.contactOrg ?? '—'} />
            <DtDd
              label="Budget"
              value={
                brief.inquiry.budgetMax
                  ? `£${(brief.inquiry.budgetMax / 100).toLocaleString()}`
                  : '—'
              }
            />
            <DtDd
              label="Due diligence"
              value={brief.inquiry.dueDiligenceScore ?? 'unknown'}
            />
          </dl>
        ) : (
          <div className="text-sm text-ovation-muted">
            No inquiry linked to this show.
          </div>
        )}
      </Panel>

      <Panel title="Crew">
        <dl className="space-y-1 text-sm">
          <DtDd
            label="Accepted"
            value={String(brief.crewEngagements.accepted)}
          />
          <DtDd label="Offered" value={String(brief.crewEngagements.offered)} />
          <DtDd
            label="Declined"
            value={String(brief.crewEngagements.declined)}
          />
          <DtDd
            label="Roles unfilled"
            value={String(brief.roleRequirements.unfilled)}
          />
        </dl>
      </Panel>

      <Panel title="Ticketing">
        {brief.ticketing.sections.length > 0 ? (
          <ul className="space-y-2 text-sm">
            {brief.ticketing.sections.map((s) => (
              <li
                key={s.id}
                className="flex items-center justify-between border-b border-ovation-ink/5 pb-2 last:border-none"
              >
                <div>
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-ovation-muted">
                    £{(s.pricePence / 100).toFixed(2)}
                  </div>
                </div>
                <div className="text-right">
                  <div>
                    {s.sold} / {s.capacity}
                  </div>
                  {s.held > 0 && (
                    <div className="text-xs text-amber-700">
                      {s.held} held
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-ovation-muted">
            No ticket sections yet. Add one to start selling.
          </div>
        )}
      </Panel>

      <Panel title="Travel">
        {brief.travelLegs.length > 0 ? (
          <ul className="space-y-2 text-sm">
            {brief.travelLegs.map((t) => (
              <li
                key={t.id}
                className="border-b border-ovation-ink/5 pb-2 last:border-none"
              >
                <div>
                  {t.origin} → {t.destination}
                </div>
                <div className="text-xs text-ovation-muted">
                  {new Date(t.departureAt).toLocaleString()} · {t.type}
                  {t.carrier && ` · ${t.carrier}`} · {t.partyCount} pax
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-ovation-muted">
            No travel legs booked yet.
          </div>
        )}
      </Panel>

      <Panel title="Email threads">
        {brief.emailThreads.items.length > 0 ? (
          <ul className="space-y-2 text-sm">
            {brief.emailThreads.items.slice(0, 6).map((t) => (
              <li
                key={t.id}
                className="flex items-start justify-between gap-2 border-b border-ovation-ink/5 pb-2 last:border-none"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{t.subject}</div>
                  <div className="text-xs text-ovation-muted">
                    {t.messageCount} messages ·{' '}
                    {new Date(t.lastMessageAt).toLocaleDateString()}
                  </div>
                </div>
                <span className="shrink-0 rounded bg-ovation-paper/60 px-2 py-0.5 text-[10px] uppercase">
                  {t.status}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-sm text-ovation-muted">
            No email threads pinned to this show.
          </div>
        )}
      </Panel>

      <Panel title="Marketing">
        <dl className="space-y-1 text-sm">
          <DtDd label="Draft" value={String(brief.marketingPosts.draft)} />
          <DtDd
            label="Scheduled"
            value={String(brief.marketingPosts.scheduled)}
          />
          <DtDd
            label="Published"
            value={String(brief.marketingPosts.published)}
          />
          <DtDd label="Failed" value={String(brief.marketingPosts.failed)} />
        </dl>
      </Panel>

      <div className="lg:col-span-2">
        <HiveCellsPanel
          showId={brief.show.id}
          stage={brief.show.stage}
          integrations={brief.show.integrations}
        />
      </div>
    </div>
  )
}

const ACTION_LINKS: Record<string, { label: string; to: string }> = {
  unfilled_role: { label: 'Manage on Tours page', to: '/tours' },
  unresponded_offer: { label: 'View talent pool', to: '/crew/pool' },
  unanswered_email: { label: 'Go to inbox', to: '/inbox' },
  stalled_broker: { label: 'View brokers', to: '/brokers' },
  missing_contract: { label: 'View pipeline', to: '/pipeline' },
  missing_travel: { label: 'Plan travel', to: '/travel' },
  missing_setlist: { label: 'Manage setlists', to: '/shows' },
  missing_debrief: { label: 'Add debrief', to: '/shows' },
  climate_blocked: { label: 'Check wellness', to: '/wellness' },
}

function ActionLink({ type, showId }: { type: string; showId: string }) {
  const action = ACTION_LINKS[type]
  if (!action) return null
  return (
    <Link
      to={type === 'missing_travel' ? `/travel` : action.to}
      className="inline-flex items-center gap-1 rounded border border-ovation-ink/20 bg-ovation-ink/5 px-2.5 py-1 text-xs font-medium text-ovation-ink hover:bg-ovation-ink/10"
    >
      {action.label}
    </Link>
  )
}

function OutstandingTab({
  items,
  isLoading,
  showId,
}: {
  items: OutstandingItem[]
  isLoading: boolean
  showId: string
}) {
  const qc = useQueryClient()

  async function dismissItem(item: OutstandingItem) {
    const reason = prompt('Why is this not applicable? (optional)')
    try {
      await artistFetch(`/shows/${showId}/outstanding/dismiss`, {
        method: 'POST',
        body: {
          itemType: item.type,
          itemKey: item.linkedEntityId,
          reason: reason || undefined,
        },
      })
      qc.invalidateQueries({ queryKey: ['show-outstanding', showId] })
    } catch {
      alert('Failed to dismiss item')
    }
  }

  if (isLoading) {
    return <div className="text-sm text-ovation-muted">Loading...</div>
  }
  if (items.length === 0) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 p-6 text-sm text-emerald-800">
        Nothing outstanding. This show is fully on track.
      </div>
    )
  }
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div
          key={`${item.type}-${i}`}
          className={cn('rounded-lg border p-4', SEVERITY_COLOUR[item.severity])}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle
              className={cn(
                'mt-0.5 h-4 w-4 shrink-0',
                SEVERITY_ICON_COLOUR[item.severity],
              )}
            />
            <div className="min-w-0 flex-1">
              <div className="font-medium">{item.title}</div>
              <div className="text-sm text-ovation-muted">{item.description}</div>
              {item.relatedDate && (
                <div className="mt-1 flex items-center gap-1 text-xs text-ovation-muted">
                  <Clock className="h-3 w-3" />
                  {new Date(item.relatedDate).toLocaleString()}
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <ActionLink type={item.type} showId={showId} />
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-1">
              <span className="text-[10px] uppercase tracking-wider text-ovation-muted">
                {item.severity}
              </span>
              <button
                type="button"
                onClick={() => dismissItem(item)}
                className="rounded border border-ovation-ink/15 px-2 py-0.5 text-[11px] text-ovation-muted hover:bg-ovation-ink/5"
                title="Mark as not applicable to this show"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function AskTab({ showId }: { showId: string }) {
  const [question, setQuestion] = useState('')
  const [messages, setMessages] = useState<
    Array<{ role: 'user' | 'assistant'; content: string }>
  >([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!question.trim()) return
    const userMsg = { role: 'user' as const, content: question }
    setMessages((m) => [...m, userMsg])
    setQuestion('')
    setSubmitting(true)
    setError(null)
    try {
      const result = await artistFetch<AskResponse>(`/shows/${showId}/ask`, {
        method: 'POST',
        body: {
          question: userMsg.content,
          history: messages,
        },
      })
      setMessages((m) => [
        ...m,
        { role: 'assistant', content: result.answer },
      ])
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Unexpected error.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex h-[600px] flex-col">
      <div className="flex-1 space-y-4 overflow-y-auto rounded-lg border border-ovation-ink/10 bg-white p-6">
        {messages.length === 0 && (
          <div className="space-y-2 text-sm text-ovation-muted">
            <p>
              Ask anything about this show. I have full context: every email
              thread, every crew engagement, the broker conversation, travel
              plans, ticket sales, marketing posts, setlist, and debrief.
            </p>
            <p>
              Try: <em>"What's still unanswered from the agent?"</em> ·{' '}
              <em>"How many venue crew do we need?"</em> ·{' '}
              <em>"Have we confirmed the rider?"</em>
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={cn(
              'flex',
              m.role === 'user' ? 'justify-end' : 'justify-start',
            )}
          >
            <div
              className={cn(
                'max-w-[80%] whitespace-pre-wrap rounded-lg px-4 py-2 text-sm',
                m.role === 'user'
                  ? 'bg-ovation-ink text-ovation-paper'
                  : 'border border-ovation-ink/10 bg-ovation-paper/40',
              )}
            >
              {m.content}
            </div>
          </div>
        ))}
        {submitting && (
          <div className="text-sm text-ovation-muted">Thinking…</div>
        )}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
      <form onSubmit={onSubmit} className="mt-4 flex gap-2">
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Ask the show…"
          className="flex-1 rounded-md border border-ovation-ink/15 px-4 py-2 text-sm focus:border-ovation-ink focus:outline-none"
        />
        <button
          type="submit"
          disabled={submitting || !question.trim()}
          className="flex items-center gap-2 rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper hover:bg-ovation-ink/90 disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          Ask
        </button>
      </form>
    </div>
  )
}

// ─── Small helpers ───────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'border-b-2 px-4 py-2 text-sm transition-colors',
        active
          ? 'border-ovation-ink font-medium text-ovation-ink'
          : 'border-transparent text-ovation-muted hover:text-ovation-ink',
      )}
    >
      {children}
    </button>
  )
}

function Panel({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-5">
      <h3 className="mb-3 text-xs uppercase tracking-wider text-ovation-muted">
        {title}
      </h3>
      {children}
    </div>
  )
}

function HeroStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string | number
}) {
  return (
    <div className="rounded-md border border-ovation-ink/10 bg-ovation-paper/30 p-3">
      <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-ovation-muted">
        {icon}
        {label}
      </div>
      <div className="mt-1 font-display text-2xl">{value}</div>
    </div>
  )
}

function DtDd({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ovation-muted">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  )
}

function StageBadge({ stage }: { stage: string }) {
  const COLOURS: Record<string, string> = {
    confirmed: 'bg-emerald-100 text-emerald-700',
    advancing: 'bg-teal-100 text-teal-700',
    completed: 'bg-slate-200 text-slate-800',
    cancelled: 'bg-rose-100 text-rose-700',
  }
  return (
    <span
      className={cn(
        'inline-flex rounded px-2 py-0.5 text-xs uppercase tracking-wider',
        COLOURS[stage] ?? 'bg-slate-100 text-slate-700',
      )}
    >
      {stage.replace(/_/g, ' ')}
    </span>
  )
}
